/* eslint-disable @typescript-eslint/no-unsafe-assignment -- react-select types are bs */

import { useEffect, useMemo, useRef } from "react";
import type { FC } from "react";
import ReactSelectCreatable from "react-select/creatable";
import { cx } from "../../../utils/classnames";
import { InputSize } from "../Input";
import { defaultSelectTheme, sizedSelectStyles } from ".";
import type { SelectProps, Option, BaseOption } from ".";

export const CreatableSelect: FC<
  Omit<SelectProps, "options" | "styles" | "formatOptionLabel"> & {
    options: Option[];
  }
> = ({
  label,
  id,
  placeholder,
  onChange,
  value,
  options,
  className,
  defaultValue,
  labelClassName,
  loading,
  size = InputSize.BASE,
  searchable,
  error,
  clearable,
  focusOnError,
  handleCreate,
  ...rest
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (error && focusOnError) {
      (
        ref as unknown as {
          current?: {
            focus?: () => void;
          };
        }
      ).current?.focus?.();
    }
  }, [error, focusOnError]);

  const mappedOptions = useMemo(() => {
    return options.reduce((opts: BaseOption[], option: Option) => {
      if ("options" in option) return [...opts, ...option.options];
      return [...opts, option];
    }, []);
  }, [options]);

  return (
    <>
      {label ? (
        <label
          className={cx(
            "text-content-700 mb-1 mr-auto block text-sm font-medium",
            labelClassName
          )}
          htmlFor={id}
        >
          {label}
        </label>
      ) : null}
      <ReactSelectCreatable
        className={cx(
          "placeholder:text-content-100 mb-2 block cursor-pointer rounded-md text-base placeholder:text-base placeholder:font-normal",
          error ? "border-danger-500" : "border-alpha-100",
          className
        )}
        defaultValue={
          (mappedOptions.find((d) => d.value === defaultValue) ??
            defaultValue) as Option
        }
        // error={error}
        id={id}
        isClearable={clearable}
        isLoading={loading}
        isMulti={false}
        isSearchable={searchable}
        name={id}
        onChange={(v) => {
          if (v && onChange) {
            onChange({
              // @ts-expect-error -- react-select types are bs
              target: { value: v.value },
              id,
              name: id
            });
          }
        }}
        onCreateOption={handleCreate}
        options={options}
        placeholder={placeholder}
        ref={ref}
        styles={sizedSelectStyles(size)}
        theme={defaultSelectTheme}
        value={mappedOptions.find((d) => d.value === value)}
        {...rest}
      />
    </>
  );
};
